import { useEffect, useMemo, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from "react-leaflet";
import { auth, db, storage } from "../firebase";
import {
  signInWithPopup, GoogleAuthProvider, signInWithEmailAndPassword,
  createUserWithEmailAndPassword, updateProfile, signOut, sendPasswordResetEmail,
} from "firebase/auth";
import {
  collection, addDoc, onSnapshot, serverTimestamp, query, orderBy
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import L from "leaflet";
import markerIcon from "../assets/incident-marker.png";
import "leaflet/dist/leaflet.css";
import "./IncidentMap.css";
import IncidentPopupContent from './IncidentPopupContent';

// --- UPDATED DEPENDENCIES ---
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import 'leaflet-geosearch/dist/geosearch.css';

const INCIDENT_CATEGORIES = ["General", "Traffic", "Weather", "Crime", "Fire", "Medical", "Utility", "Event"];
const INCIDENT_SEVERITIES = ["Low", "Medium", "High", "Critical"];
const INCIDENT_STATUSES = ["new", "verified", "resolved"];
const AUTO_MARQUEE_RADIUS_STEPS = [10, 50, 100, 200, 500];
const MANUAL_MARQUEE_RADIUS_OPTIONS = [50, 100, 200, 500];
const MAP_SEARCH_ZOOM_FALLBACK = 18;

const isValidLocation = (location) =>
  location &&
  typeof location.lat === "number" &&
  typeof location.lng === "number" &&
  Number.isFinite(location.lat) &&
  Number.isFinite(location.lng);

const toRadians = (degrees) => (degrees * Math.PI) / 180;

const distanceMiles = (from, to) => {
  const earthRadiusMiles = 3958.8;
  const latDiff = toRadians(to.lat - from.lat);
  const lngDiff = toRadians(to.lng - from.lng);
  const fromLat = toRadians(from.lat);
  const toLat = toRadians(to.lat);
  const haversineA =
    Math.sin(latDiff / 2) ** 2 +
    Math.cos(fromLat) * Math.cos(toLat) * Math.sin(lngDiff / 2) ** 2;
  const haversineC = 2 * Math.atan2(Math.sqrt(haversineA), Math.sqrt(1 - haversineA));
  return earthRadiusMiles * haversineC;
};

// --- UPDATED COMPONENT: Location Search ---
// This now correctly integrates leaflet-geosearch with modern React
const LocationSearchField = () => {
  const map = useMap();

  useEffect(() => {
    const provider = new OpenStreetMapProvider();

    const searchControl = new GeoSearchControl({
      provider,
      style: 'bar',
      showMarker: true,
      showPopup: false,
      autoClose: true,
      retainZoomLevel: false,
      animateZoom: true,
      zoomLevel: MAP_SEARCH_ZOOM_FALLBACK,
      searchLabel: 'Search for a location...',
    });

    map.addControl(searchControl);

    const zoomToSearchResult = ({ location }) => {
      if (!location) return;
      const maxZoom = map.getMaxZoom();
      const targetZoom = Number.isFinite(maxZoom) ? maxZoom : MAP_SEARCH_ZOOM_FALLBACK;
      map.setView([location.y, location.x], targetZoom, { animate: true });
    };

    map.on("geosearch/showlocation", zoomToSearchResult);

    return () => {
      map.off("geosearch/showlocation", zoomToSearchResult);
      map.removeControl(searchControl);
    };
  }, [map]); // Re-run effect if map instance changes

  return null; // This component doesn't render anything itself
};

// --- NEW COMPONENT: Add Incident Modal ---
function AddIncidentModal({ position, onClose, onSubmit, onShowMessage }) {
    const [description, setDescription] = useState("");
    const [category, setCategory] = useState(INCIDENT_CATEGORIES[0]);
    const [severity, setSeverity] = useState(INCIDENT_SEVERITIES[1]);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const uploadInputId = "image-upload";
    const captureInputId = "image-capture";

    useEffect(() => {
      return () => {
        if (imagePreview) {
          URL.revokeObjectURL(imagePreview);
        }
      };
    }, [imagePreview]);

    const updateSelectedImage = (file) => {
        if (file) {
            if (imagePreview) {
              URL.revokeObjectURL(imagePreview);
            }
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        } else {
            setImageFile(null);
            if (imagePreview) {
              URL.revokeObjectURL(imagePreview);
            }
            setImagePreview(null);
        }
    };

    const handleImageChange = (e) => {
        updateSelectedImage(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!description.trim()) {
            onShowMessage({
              title: "Description Required",
              message: "Please enter a description before submitting the incident.",
              tone: "warning",
            });
            return;
        }
        const wasSubmitted = await onSubmit({ description, category, severity, imageFile });
        if (wasSubmitted) {
            onClose(); // Close modal after successful submission
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal add-incident-modal" onClick={(e) => e.stopPropagation()}>
                <h3>Report a New Incident</h3>
                <p>Location: {position.lat.toFixed(5)}, {position.lng.toFixed(5)}</p>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="description">Description</label>
                        <textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            required
                            placeholder="What's happening?"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="category">Category</label>
                        <select id="category" value={category} onChange={(e) => setCategory(e.target.value)}>
                          {INCIDENT_CATEGORIES.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="severity">Severity</label>
                        <select id="severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
                          {INCIDENT_SEVERITIES.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Optional Image</label>
                        <div className="file-input-actions">
                            <label htmlFor={uploadInputId} className="file-input-label">
                                Choose an image
                            </label>
                            <label htmlFor={captureInputId} className="file-input-label">
                                Take a photo
                            </label>
                        </div>
                        <input id={uploadInputId} type="file" accept="image/*" onChange={handleImageChange} />
                        <input
                            id={captureInputId}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={handleImageChange}
                        />
                        <div className="file-input-selection">
                            {imageFile ? imageFile.name : "No image selected"}
                        </div>
                        {imagePreview && (
                            <div className="image-preview">
                                <img src={imagePreview} alt="Preview" />
                            </div>
                        )}
                    </div>
                    <div className="modal-form-actions">
                        <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
                        <button type="submit" className="submit-btn">Submit Incident</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function RecenterMap({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo([center.lat, center.lng], 15, { animate: true });
    }
  }, [center, map]);
  return null;
}

const IncidentIcon = new L.Icon({
  iconUrl: markerIcon,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

export const formatTimestamp = (timestamp) => {
 if (timestamp && typeof timestamp.toDate === 'function') {
  try {
      const date = timestamp.toDate();
      return date.toLocaleString();
  } catch (error) {
      console.error("Error formatting timestamp:", error);
      return "Invalid date";
  }
}
return "Unknown time";
};

function AddMarker({ onMapClick, onShowMessage }) {
  useMapEvents({
    click(e) {
      if (!auth.currentUser) {
        onShowMessage({
          title: "Login Required",
          message: "You must be logged in to report an incident.",
          tone: "warning",
        });
        return;
      }
      onMapClick(e.latlng);
    },
  });
  return null;
}

function IncidentFilters({ filters, onChange, totalCount, visibleCount }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section className={`filter-panel ${isExpanded ? "is-expanded" : "is-collapsed"}`}>
      <div className="filter-panel-header">
        <button
          type="button"
          className="filter-toggle-btn"
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((previous) => !previous)}
        >
          {isExpanded ? "Hide Filters" : "Show Filters"}
        </button>
        <p className="filter-summary">
          Showing {visibleCount} of {totalCount} incidents
        </p>
      </div>
      {isExpanded && (
        <div className="filter-bar">
          <input
            type="text"
            value={filters.search}
            onChange={(e) => onChange({ ...filters, search: e.target.value })}
            placeholder="Search descriptions"
          />
          <select value={filters.category} onChange={(e) => onChange({ ...filters, category: e.target.value })}>
            <option value="all">All categories</option>
            {INCIDENT_CATEGORIES.map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          <select value={filters.severity} onChange={(e) => onChange({ ...filters, severity: e.target.value })}>
            <option value="all">All severities</option>
            {INCIDENT_SEVERITIES.map((severity) => (
              <option key={severity} value={severity}>{severity}</option>
            ))}
          </select>
          <select value={filters.status} onChange={(e) => onChange({ ...filters, status: e.target.value })}>
            <option value="all">All statuses</option>
            {INCIDENT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>
          <select value={filters.timeWindowHours} onChange={(e) => onChange({ ...filters, timeWindowHours: Number(e.target.value) })}>
            <option value={0}>All time</option>
            <option value={1}>Last 1h</option>
            <option value={6}>Last 6h</option>
            <option value={18}>Last 18h</option>
          </select>
          <select value={filters.marqueeRadius} onChange={(e) => onChange({ ...filters, marqueeRadius: e.target.value })}>
            <option value="auto">Nearby (10 mi)</option>
            {MANUAL_MARQUEE_RADIUS_OPTIONS.map((radius) => (
              <option key={radius} value={String(radius)}>
                {radius} mi
              </option>
            ))}
            <option value="all">All</option>
          </select>
          <button type="button" onClick={() => onChange({ search: "", category: "all", severity: "all", status: "all", timeWindowHours: 18, marqueeRadius: "auto" })}>
            Reset
          </button>
        </div>
      )}
      <p className="filter-summary-mobile">
        Showing {visibleCount} of {totalCount} incidents
      </p>
    </section>
  );
}

function AppMessageModal({ title, message, tone = "info", onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal app-message-modal app-message-modal-${tone}`} onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <p className="modal-body-copy">{message}</p>
        <div className="modal-form-actions">
          <button type="button" className="submit-btn" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

function IncidentMarquee({ incidents, onSelectIncident }) {
  const marqueeRef = useRef(null);
  const isDraggingRef = useRef(false);
  const isHoveredRef = useRef(false);
  const startXRef = useRef(0);
  const scrollXRef = useRef(0);
  const velocityRef = useRef(0);
  const lastTimeRef = useRef(Date.now());
  const animationFrameIdRef = useRef(null);
  const BASE_AUTO_SCROLL_SPEED = 1;
  const autoScrollSpeed = incidents.length > 0 && incidents.length < 3
    ? BASE_AUTO_SCROLL_SPEED / 4
    : BASE_AUTO_SCROLL_SPEED;

  const repositionItems = () => {
    const marquee = marqueeRef.current;
    if (!marquee || marquee.children.length === 0) return;
    while (true) {
      const firstChild = marquee.firstElementChild;
      if (!firstChild) break;
      const marginRight = parseFloat(window.getComputedStyle(firstChild).marginRight) || 0;
      const firstChildWidth = firstChild.offsetWidth + marginRight;
      if (-scrollXRef.current >= firstChildWidth) {
        marquee.appendChild(firstChild);
        scrollXRef.current += firstChildWidth;
      } else { break; }
    }
    while (true) {
      const lastChild = marquee.lastElementChild;
      if (!lastChild) break;
      const marginRight = parseFloat(window.getComputedStyle(lastChild).marginRight) || 0;
      const lastChildWidth = lastChild.offsetWidth + marginRight;
      if (scrollXRef.current > 0) {
        marquee.insertBefore(lastChild, marquee.firstElementChild);
        scrollXRef.current -= lastChildWidth;
      } else { break; }
    }
  };

  useEffect(() => {
    const marquee = marqueeRef.current;
    if (!marquee) return;

    const animateMarquee = () => {
      const now = Date.now();
      const deltaTime = (now - lastTimeRef.current) / 1000;
      lastTimeRef.current = now;

      if (!isDraggingRef.current && !isHoveredRef.current) {
        const friction = 0.95;
        velocityRef.current = velocityRef.current * friction + autoScrollSpeed * (1 - friction);
        scrollXRef.current -= velocityRef.current * deltaTime * 100;
      }
      
      repositionItems();
      marquee.style.transform = `translate3d(${scrollXRef.current}px, 0, 0)`;
      animationFrameIdRef.current = requestAnimationFrame(animateMarquee);
    };

    const stopAnimation = () => {
      if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }
    const startAnimation = () => {
      if(!animationFrameIdRef.current) {
        lastTimeRef.current = Date.now();
        animationFrameIdRef.current = requestAnimationFrame(animateMarquee);
      }
    }

    const handleMouseDown = (e) => {
      isDraggingRef.current = true;
      startXRef.current = e.clientX;
      stopAnimation();
    };

    const handleMouseMove = (e) => {
      if (!isDraggingRef.current) return;
      const delta = e.clientX - startXRef.current;
      marquee.style.transform = `translate3d(${scrollXRef.current + delta}px, 0, 0)`;
    };

    const handleMouseUp = (e) => {
      if (!isDraggingRef.current) return;
      const delta = e.clientX - startXRef.current;
      scrollXRef.current += delta;
      velocityRef.current = -delta * 0.15;
      isDraggingRef.current = false;
      startAnimation();
    };
    
    // Simplified touch handlers
    const handleTouchStart = (e) => handleMouseDown(e.touches[0]);
    const handleTouchMove = (e) => handleMouseMove(e.touches[0]);
    const handleTouchEnd = (e) => handleMouseUp(e.changedTouches[0]);

    marquee.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    marquee.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", handleTouchEnd);
    
    startAnimation();

    return () => {
      marquee.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      marquee.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      stopAnimation();
    };
  }, [autoScrollSpeed]);

  return (
    <div className="marquee-container">
      <div ref={marqueeRef} className="marquee">
        {incidents.map((incident, index) => (
          <div
            key={incident.id || index}
            className={`marquee-item ${
              incident.votes > 0
                ? "marquee-item-trending"
                : incident.votes < 0
                  ? "marquee-item-cooling"
                  : "marquee-item-neutral"
            }`}
            onMouseEnter={() => {
              isHoveredRef.current = true;
            }}
            onMouseLeave={() => {
              isHoveredRef.current = false;
            }}
            onClick={() => onSelectIncident(incident)}
          >
            <p className="marquee-title">{incident.title || "Incident Report"}</p>
            <p className="marquee-description">{incident.description}</p>
            <p className="marquee-meta">
              By {incident.userName || "Anonymous"} | Votes: {incident.votes}
            </p>
            <p className="marquee-meta">
              {incident.category || "General"} | {incident.severity || "Medium"} | {((incident.status || "new").charAt(0).toUpperCase() + (incident.status || "new").slice(1))}
            </p>
              <p className="marquee-time">
                {incident.timestamp ? incident.timestamp.toDate().toLocaleString()
                    : "Unknown time"}
              </p>
            </div>
          ))}
        </div>
    </div>
  );
}

function LoginModal({ onClose, onShowMessage }) {
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");

  const handleGoogleLogin = async () => {
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      onClose();
    } catch (error) {
      console.error("Google login error:", error);
      onShowMessage({
        title: "Google Login Failed",
        message: "Google sign-in could not be completed right now. Please try again.",
        tone: "error",
      });
    }
  };

  const handleEmailAuth = async () => {
    if (isRegistering) {
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(userCredential.user, { displayName: username });
        onClose();
      } catch (error) {
        console.error("Email registration error:", error);
        onShowMessage({
          title: "Registration Failed",
          message: "Your account could not be created right now. Please try again.",
          tone: "error",
        });
      }
    } else {
      try {
        await signInWithEmailAndPassword(auth, email, password);
        onClose();
      } catch (error) {
        console.error("Email login error:", error);
        onShowMessage({
          title: "Login Failed",
          message: "Email login could not be completed. Please check your details and try again.",
          tone: "error",
        });
      }
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      onShowMessage({
        title: "Email Required",
        message: "Please enter your email address first so a reset link can be sent.",
        tone: "warning",
      });
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      onShowMessage({
        title: "Reset Email Sent",
        message: "Password reset email sent. Please check your inbox.",
        tone: "success",
      });
    } catch (error) {
      console.error("Password reset error:", error);
      onShowMessage({
        title: "Reset Failed",
        message: "The password reset email could not be sent right now. Please try again.",
        tone: "error",
      });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="modal-close">X</button>
        <h3>{isRegistering ? "Register" : "Login"}</h3>
        <button onClick={handleGoogleLogin} className="modal-button google">Login with Google</button>
        <div className="modal-divider">or</div>
        <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="modal-input" />
        <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="modal-input" />
        <button onClick={handleForgotPassword} className="modal-link">Forgot your password?</button>
        {isRegistering && ( <input type="text" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} className="modal-input" /> )}
        <button onClick={handleEmailAuth} className="modal-button email">{isRegistering ? "Register" : "Login with Email"}</button>
        <div className="modal-toggle">
          <span>{isRegistering ? "Already have an account?" : "Don't have an account?"}</span>
          <button onClick={() => setIsRegistering(!isRegistering)} className="modal-link">{isRegistering ? "Login" : "Register"}</button>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [user, setUser] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    category: "all",
    severity: "all",
    status: "all",
    timeWindowHours: 18,
    marqueeRadius: "auto",
  });
  const [addingIncident, setAddingIncident] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [centerLocation, setCenterLocation] = useState(null);
  const [selectedIncidentId, setSelectedIncidentId] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [messageModal, setMessageModal] = useState(null);
  const markerRefs = useRef(new Map());

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(setUser);
    const incidentsQuery = query(collection(db, "incidents"), orderBy("timestamp", "desc"));
    const unsubscribeIncidents = onSnapshot(incidentsQuery, (snapshot) => {
      setIncidents(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return () => {
      unsubscribeAuth();
      unsubscribeIncidents();
    };
  }, []);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
        setCurrentLocation(loc);
        setCenterLocation(loc);
      },
      (error) => { console.error("Error fetching geolocation:", error); }
    );
  }, []);

  const visibleIncidents = useMemo(() => {
    const now = Date.now();
    return incidents.filter((incident) => {
      if (!isValidLocation(incident.location)) {
        return false;
      }

      if (filters.search && !(incident.description || "").toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }

      if (filters.category !== "all" && incident.category !== filters.category) {
        return false;
      }

      if (filters.severity !== "all" && incident.severity !== filters.severity) {
        return false;
      }

      const status = incident.status || "new";
      if (filters.status !== "all" && status !== filters.status) {
        return false;
      }

      if (filters.timeWindowHours > 0 && incident.timestamp?.toDate) {
        const ageMs = now - incident.timestamp.toDate().getTime();
        if (ageMs > filters.timeWindowHours * 60 * 60 * 1000) {
          return false;
        }
      }

      return true;
    });
  }, [incidents, filters]);

  const marqueeIncidents = useMemo(() => {
    if (!currentLocation || visibleIncidents.length === 0) {
      return visibleIncidents;
    }

    const inRadius = (radiusMiles) =>
      visibleIncidents.filter((incident) => distanceMiles(currentLocation, incident.location) <= radiusMiles);

    if (filters.marqueeRadius === "all") {
      return visibleIncidents;
    }

    if (filters.marqueeRadius !== "auto") {
      return inRadius(Number(filters.marqueeRadius));
    }

    for (const radiusMiles of AUTO_MARQUEE_RADIUS_STEPS) {
      const matches = inRadius(radiusMiles);
      if (matches.length > 0) {
        return matches;
      }
    }

    return visibleIncidents;
  }, [currentLocation, visibleIncidents, filters.marqueeRadius]);

  const handleAddIncident = async ({ description, category, severity, imageFile }) => {
    if (!user || !addingIncident) return;
    let imageUrl = null;
    if (imageFile) {
      try {
        const storageRef = ref(storage, `incidentImages/${user.uid}_${Date.now()}_${imageFile.name}`);
        const snapshot = await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(snapshot.ref);
      } catch (error) {
        console.error("Error uploading image: ", error);
        setMessageModal({
          title: "Image Upload Failed",
          message: "The image could not be uploaded. The incident will still be reported without it.",
          tone: "warning",
        });
      }
    }
    try {
      const incidentPayload = {
        location: { lat: addingIncident.lat, lng: addingIncident.lng },
        description,
        category,
        severity,
        status: "new",
        userId: user.uid,
        userName: user.displayName || "Anonymous",
        votes: 0,
        upvotes: 0,
        downvotes: 0,
        timestamp: serverTimestamp(),
      };
      if (typeof imageUrl === "string" && imageUrl.length > 0) {
        incidentPayload.imageUrl = imageUrl;
      }

      await addDoc(collection(db, "incidents"), incidentPayload);
      setMessageModal({
        title: "Incident Reported",
        message: "Your incident has been posted successfully.",
        tone: "success",
      });
      return true;
    } catch (error) {
      console.error("Error adding incident: ", error);
      setMessageModal({
        title: "Report Failed",
        message: "The incident could not be reported right now. Please try again.",
        tone: "error",
      });
      return false;
    }
  };

  const handleSelectIncident = (incident) => {
    if (isValidLocation(incident.location)) {
      setCenterLocation({ lat: incident.location.lat, lng: incident.location.lng });
      setSelectedIncidentId(incident.id);
    } else {
      setMessageModal({
        title: "Invalid Incident Location",
        message: "This incident does not have a valid map location.",
        tone: "warning",
      });
    }
  };

  useEffect(() => {
    if (!selectedIncidentId) {
      return;
    }

    const marker = markerRefs.current.get(selectedIncidentId);
    if (marker) {
      marker.openPopup();
    }
  }, [selectedIncidentId, centerLocation, visibleIncidents]);

  const handleCenterOnMyLocation = () => {
    if (currentLocation) {
      setCenterLocation({ ...currentLocation });
    } else {
      setMessageModal({
        title: "Location Unavailable",
        message: "Your current location is not available yet.",
        tone: "warning",
      });
    }
  };

  return (
    <div className="app-container">
      <header className="app-header">
        <h2>Incident Map</h2>
        <div>
          {user ? (
            <button onClick={() => signOut(auth)} className="logout-btn">Logout</button>
          ) : (
            <button onClick={() => setShowLoginModal(true)} className="login-btn">Login / Register</button>
          )}
          <button onClick={handleCenterOnMyLocation} className="location-btn" disabled={!currentLocation}>
            {currentLocation ? "My Location" : "Locating..."}
          </button>
        </div>
      </header>
      
      <main className="app-main">
        <IncidentFilters
          filters={filters}
          onChange={setFilters}
          totalCount={incidents.length}
          visibleCount={marqueeIncidents.length}
        />
        <div className="map-container-wrapper">
          <MapContainer center={currentLocation || [51.505, -0.09]} zoom={13} style={{ height: "60vh", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <LocationSearchField />
            <AddMarker
              onMapClick={(latlng) => setAddingIncident(latlng)}
              onShowMessage={setMessageModal}
            />
            {visibleIncidents.map((incident) => (
              <Marker
                key={incident.id}
                position={[incident.location.lat, incident.location.lng]}
                icon={IncidentIcon}
                ref={(markerInstance) => {
                  if (markerInstance) {
                    markerRefs.current.set(incident.id, markerInstance);
                  } else {
                    markerRefs.current.delete(incident.id);
                  }
                }}
              >
                <Popup>
                  <IncidentPopupContent incident={incident} user={user} />
                </Popup>
              </Marker>
            ))}
            <RecenterMap center={centerLocation} />
          </MapContainer>
        </div>
        
        <IncidentMarquee incidents={marqueeIncidents} onSelectIncident={handleSelectIncident} />
      </main>

      <footer className="app-footer">
        <a href="about.html">About Sitrep Now</a>
        <p className="copyright">&copy; Sitrep Now 2026</p>
      </footer>

      {addingIncident && (
        <AddIncidentModal
            position={addingIncident}
            onClose={() => setAddingIncident(null)}
            onSubmit={handleAddIncident}
            onShowMessage={setMessageModal}
        />
      )}

      {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} onShowMessage={setMessageModal} />}
      {messageModal && (
        <AppMessageModal
          title={messageModal.title}
          message={messageModal.message}
          tone={messageModal.tone}
          onClose={() => setMessageModal(null)}
        />
      )}
    </div>
  );
}
